import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL } from '@utils/api/endpoints';
import { QueryKeys } from '@utils/api/query-keys';

/**
 * Response from the backend translation-quote endpoint (#2241).
 *
 * `cost_amount` arrives as a decimal string (pydantic Decimal → JSON).
 * `eligible` and `has_charge` are deliberately separate: a free plan
 * is eligible yet has no charge, and the UI needs to tell them apart
 * (show "Free" vs. a price).
 */
export type TranslationQuote = {
  characters: number;
  cost_amount: string | null;
  credit_amount?: string;
  invoice_amount?: string;
  currency: string | null;
  eligible: boolean;
  has_charge: boolean;
};

type TranslationQuoteResponse = {
  result: TranslationQuote;
};

export async function fetchTranslationQuote(
  sessionId: string,
  documentId: string
): Promise<TranslationQuoteResponse> {
  const { data } = await Axios.get(
    GET_URL(sessionId, documentId).translationQuote
  );
  return data;
}

export function useTranslationQuote(
  sessionId?: string,
  documentId?: string,
  enabled = true
) {
  return useQuery<TranslationQuoteResponse, AxiosError>(
    QueryKeys.translationQuote(sessionId ?? '', documentId ?? ''),
    () => fetchTranslationQuote(sessionId!, documentId!),
    {
      enabled: enabled && !!sessionId && !!documentId,
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: false
    }
  );
}
